import { cloneElement, type ReactElement } from 'react'

/**
 * Renders a caller's own element carrying this component's styling.
 *
 * Base UI's `render` prop for the components that are not Base UI components,
 * and for the one that is but must not be. A Button asked to render an anchor
 * is an anchor: routing it through Base UI's Button stamps `role="button"` and
 * `tabindex="0"` onto it, which takes the link semantics away from exactly the
 * users who depend on them.
 *
 * `cloneElement` rather than rebuilding the element object, which is what three
 * components did independently. A React element carries `key` and `ref` outside
 * `props`, so a hand-built copy drops them without saying so.
 */
export const renderAs = (element: ReactElement, props: Record<string, unknown>): ReactElement =>
    cloneElement(element, defined(props))

/**
 * An undefined prop is one the component did not set, not one it wants cleared.
 *
 * `cloneElement` overwrites with whatever it is handed, undefined included, so
 * a component spreading its own optional props erases the same prop on the
 * caller's element. A test id on an application's own router link disappeared
 * exactly that way: the component had no test id of its own, and passing that
 * absence along deleted the one the caller had written.
 */
const defined = (props: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined))
